import styles from '@dazn/lp-web/src/components/BannerSlider/MobileSlider/mobile-slider.module.css';
import {getCards} from '@dazn/lp-web/src/components/BannerSlider/MobileSlider/mobile-slider.utils';
import MobileSliderItem from '@dazn/lp-web/src/components/BannerSlider/MobileSlider/MobileSliderItem';
import Pagination from '@dazn/lp-web/src/components/BannerSlider/MobileSlider/Pagination';
import {AnimatePresence} from 'framer-motion';
import React, {useEffect, useRef, useState} from 'react';
import {
    ExitTransforms,
    IMobileSliderProps,
    Swipe
} from '@dazn/lp-web/src/components/BannerSlider/MobileSlider/mobile-slider.types';
import {useVideoBannerContext} from '@dazn/lp-web/src/components/VideoBanner/VideoBannerContext';
import {
    checkIfSponsorshipLogoEnabled,
    checkIfSSAdsEnabledForLP,
    getIsNativePlatform,
    getLogoSlotPath
} from '@dazn/lp-web/src/features/checkIfSponsorshipLogoEnabled';
import {SponsorshipAds} from '@dazn/lp-web/src/components/SponsorShipAds/SponsorshipAds';
import {getPageId} from '@dazn/lp-web/src/utils/pageUtils';
import classNames from 'classnames';
import {bannerTypes} from '@dazn/lp-web/src/constants/bannerTypes.constants';
import {MobileBannerDesignTypes} from '@dazn/lp-web/src/types';

const exitTransForms: ExitTransforms = new Map();

const MobileSlider: React.FC<IMobileSliderProps> = (props) => {
    const {banners, activeIndex, setActiveIndex} = props;
    const {isPlayedOrSkiped, setIsPlayedOrSkiped} = useVideoBannerContext();
    const containsVideoBackground = banners?.some(
        (item) => item.backgroundType === 'video'
    );
    const shouldStopAutoplay = !isPlayedOrSkiped && containsVideoBackground;
    const [cards, setCards] = useState(getCards(banners || []));
    const [direction, setDirection] = useState('');
    const [touched, setTouched] = useState(false);
    const autoScrollRef = useRef<NodeJS.Timeout | number | null>(null);
    const isSingleBanner = cards.length === 1;

    const timeDelay = 20000;
    const createInterval = (delay: number) => {
        if (autoScrollRef.current) {
            clearInterval(autoScrollRef.current); // Clear the previous interval
        }
        autoScrollRef.current = setInterval(() => {
            if (!touched) {
                handleSwipe({
                    id: 1000,
                    direction: 'left',
                    endTransform: {
                        x: 0,
                        rotate: 0
                    }
                });
            }
        }, delay);
    };

    const handleTouchStart = () => {
        setTouched(true);
    };

    const handleTouchEnd = () => {
        setTouched(false);
        setIsPlayedOrSkiped(true);
    };

    const handleSwipe = ({id, direction, endTransform}: Swipe) => {
        if (endTransform) {
            exitTransForms.set(id, endTransform);
        }
        setDirection(direction);

        const currentCard = cards[cards.length - 1];

        setActiveIndex((prevIndex) => {
            return prevIndex === banners?.length - 1 ? 0 : prevIndex + 1;
        });

        setCards(cards.slice(0, -1));

        setTimeout(() => {
            if (endTransform) {
                exitTransForms.delete(id);
            }
            setCards((prevCards: any[]) => [currentCard, ...prevCards]);
        }, 1200);
    };

    const visibleCards = {
        [`${cards.length - 1}`]: 'first',
        [`${cards.length - 2}`]: 'second',
        [`${cards.length - 3}`]: 'third'
    };

    useEffect(() => {
        if (isSingleBanner || shouldStopAutoplay) return;

        if (!autoScrollRef.current) {
            createInterval(timeDelay);
        }
        return () => {
            if (autoScrollRef.current) {
                clearInterval(autoScrollRef.current);
            }
            autoScrollRef.current = null;
        };
    }, [activeIndex, exitTransForms, cards, touched]);

    useEffect(() => {
        // This effect updates the interval when the video banner is there
        if (!isSingleBanner && !shouldStopAutoplay) {
            createInterval(timeDelay); // Update delay based on shouldStopAutoplay
        }
        if (shouldStopAutoplay) {
            if (autoScrollRef.current) {
                clearInterval(autoScrollRef.current);
            }
            autoScrollRef.current = null;
        }
    }, [shouldStopAutoplay]);

    const nativeAppName: string = (window.dazn as ExtendedDAZN).native.appName;

    const shouldRenderSponsorshipLogo = () => {
        // while in native devices the pathname would be 'welcome/nfl?isNativeSession=true&platform=android&manufacturer=samsung'
        if (getPageId() === 'nfl' || window.location.href.includes('nfl')) {
            return true;
        }
        return false;
    };

    const checkIfNativePlatform = () => {
        if (checkIfSponsorshipLogoEnabled() && shouldRenderSponsorshipLogo()) {
            return nativeAppName === '' || getIsNativePlatform(nativeAppName);
        }
        return false;
    };

    const checkIfSSAdsEnabledForLandingPages = () => {
        if (checkIfNativePlatform()) {
            const effectiveAppName =
                nativeAppName.trim() === '' ? 'web' : nativeAppName;
            const isLandingPagesEnabled =
                checkIfSSAdsEnabledForLP(effectiveAppName);
            return isLandingPagesEnabled;
        }
        return false;
    };

    const normalizeString = (str?: string) => str?.toLowerCase() || '';

    const findBannerIndex = (banners: any[], key: string, value: string) =>
        banners.findIndex(
            (banner: any) =>
                normalizeString(banner?.[key]) === normalizeString(value)
        );

    const isRotatingContentBanner =
        normalizeString(banners[0]?.bannerType) ===
        normalizeString(bannerTypes.ROTATING_CONTENT);

    const isFullScreenBannerDesign =
        normalizeString(banners[0]?.mobileBannerDesignType) ===
        normalizeString(MobileBannerDesignTypes.FULL_SCREEN_TYPE);

    const rotatingBannerIndex = findBannerIndex(
        banners,
        'bannerType',
        bannerTypes.ROTATING_CONTENT
    );
    const fullScreenBannerIndex = findBannerIndex(
        banners,
        'mobileBannerDesignType',
        MobileBannerDesignTypes.FULL_SCREEN_TYPE
    );

    const shouldShowFullScreenDesign =
        isRotatingContentBanner || isFullScreenBannerDesign;

    const haveFullScreenBannerIndex =
        fullScreenBannerIndex >= 0
            ? fullScreenBannerIndex
            : rotatingBannerIndex;

    return (
        <main
            className={classNames(styles.mobileHeroBannerSlider, {
                [styles.rotatingMobileHeroBannerSlider]:
                    shouldShowFullScreenDesign
            })}
        >
            <div
                className={classNames(styles.bannerCardsWrapper, {
                    [styles.roatingBannerCardsWrapper]:
                        shouldShowFullScreenDesign
                })}
            >
                <AnimatePresence>
                    {cards?.map((card: any, i: number) => {
                        return (
                            <MobileSliderItem
                                touched={touched}
                                data={card}
                                key={card?.id}
                                direction={direction}
                                id={card?.id}
                                isDraggable={
                                    i === cards.length - 1 && !isSingleBanner
                                }
                                onTouchStart={handleTouchStart}
                                onTouchEnd={handleTouchEnd}
                                onSwipe={handleSwipe}
                                animate={visibleCards[`${i}`]}
                                activeIndex={activeIndex}
                                rotatingBannerIndex={haveFullScreenBannerIndex}
                            />
                        );
                    })}
                </AnimatePresence>
            </div>
            {!isSingleBanner && (
                <Pagination
                    activeIndex={activeIndex}
                    count={banners?.length}
                    isRotatingContentBanner={shouldShowFullScreenDesign}
                />
            )}
            {cards.length > 0 &&
                checkIfNativePlatform() &&
                checkIfSSAdsEnabledForLandingPages() && (
                    <SponsorshipAds
                        index={activeIndex}
                        slotPath={getLogoSlotPath()}
                    />
                )}
        </main>
    );
};

export {MobileSlider, exitTransForms};
